/* eslint-disable no-nested-ternary */
import { usePostFeedbackMutation } from '@/api/queries/feedback';
import { PlanDto } from '@/utils/data/data-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  BillingFrequency,
  colors,
  ControlledField,
  formatMoney,
  RadioControlGroup,
  RadioControlGroupOptions,
  SelectInputField,
  SelectOption,
} from 'remote/global';
import { useOperatorCurrency } from '@/api/queries/users';
import PaperLayout from '../../PaperLayout';
import { operatorAccountPlanSchema, OperatorAccountPlanSchemaType } from './types';

function AccountSubscriptionPlanForm({
  defaultValues,
  plans,
}: {
  defaultValues?: Partial<OperatorAccountPlanSchemaType>;
  plans: PlanDto[];
}) {
  const tInstance = useTranslation();
  const { t, i18n } = tInstance;
  const {
    control,
    handleSubmit,
    getValues,
    watch,
    formState: { isSubmitting, isValidating, isLoading, isDirty },
  } = useForm<OperatorAccountPlanSchemaType>({
    defaultValues,
    resolver: zodResolver(operatorAccountPlanSchema),
  });
  const postFeedbackMutation = usePostFeedbackMutation();
  const [currentPlanId, currentBillingFrequency] = watch(['planId', 'billingFrequency']);

  const selectedPlan = plans.find((p) => p.id === currentPlanId);

  const isPlanUndefined = defaultValues?.planId == null;
  const isSubmitDisabled = !isDirty || isSubmitting || isValidating || isLoading;
  const currency = useOperatorCurrency();

  const onValid = async (data: OperatorAccountPlanSchemaType) => {
    const message = `Operator is requesting for a plan upgrade: ${selectedPlan?.name}; Billing frequency: ${data.billingFrequency}`;
    await postFeedbackMutation.mutateAsync({
      ...data,
      message,
    });
  };

  const handleRequestCancelPlan = async () => {
    const previousPlan = plans.find((p) => p.id === defaultValues?.planId)!;

    const message = `Operator is requesting to cancel their "${previousPlan.name}" subscription plan`;
    await postFeedbackMutation.mutateAsync({
      ...getValues(),
      message,
    });
  };

  const planOptions: SelectOption[] = plans.map(({ name, id: value }) => ({ label: name, value }));

  if (postFeedbackMutation.isSuccess) {
    return (
      <PaperLayout
        title={t('SubscriptionView.subscription.title')}
        subtitle={t('SubscriptionView.subscription.subtitle')}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            my: '2rem',
            justifyItems: 'center',
            alignItems: 'center',
          }}
        >
          <CheckCircle sx={{ color: colors.activeGreen, width: 36, height: 36 }} />
          <Typography sx={{ color: colors.activeGreen }}>
            {t('SubscriptionView.subscription.requestSuccessful')}
          </Typography>
        </Box>
      </PaperLayout>
    );
  }

  const billingFrequencyOptions = [
    {
      label: t('terms.attributes.subscription.plan.monthly'),
      value: BillingFrequency.MONTHLY,
    },
    {
      label: t('terms.attributes.subscription.plan.yearly'),
      value: BillingFrequency.YEARLY,
    },
  ];

  return (
    <PaperLayout
      title={t('SubscriptionView.subscription.title')}
      subtitle={t('SubscriptionView.subscription.subtitle')}
    >
      <form onSubmit={handleSubmit(onValid)}>
        <ControlledField
          control={control}
          name='planId'
          render={(renderProps) => <SelectInputField {...renderProps} options={planOptions} />}
        />
        {selectedPlan != null && (
          <ControlledField
            control={control}
            name='billingFrequency'
            render={(renderProps) => (
              <RadioControlGroup renderProps={renderProps} radioGroupProps={{ row: true }}>
                <RadioControlGroupOptions
                  renderProps={renderProps}
                  options={billingFrequencyOptions}
                />
              </RadioControlGroup>
            )}
          />
        )}
        {selectedPlan != null && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '1rem',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'inline', color: colors.gray3 }}>
                {t('SubscriptionView.subscription.plan.planFee')}
              </Box>
              <Box sx={{ display: 'inline' }}>
                <>
                  {currentBillingFrequency === BillingFrequency.MONTHLY &&
                    `${formatMoney(selectedPlan.monthlyPriceExclVat, currency, i18n.language)}/${t(
                      'terms.attributes.price.perMonth'
                    ).toLowerCase()}`}
                  {currentBillingFrequency === BillingFrequency.YEARLY
                    ? selectedPlan.yearlyPriceExclVat != null
                      ? `${formatMoney(
                          selectedPlan.yearlyPriceExclVat,
                          currency,
                          i18n.language
                        )}/${t('terms.attributes.price.perYear').toLowerCase()}`
                      : t('terms.attributes.queryForPricing')
                    : currentBillingFrequency == null
                    ? t('terms.attributes.selectBillingFrequency')
                    : null}
                </>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '1rem',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'inline', color: colors.gray3 }}>
                {t('SubscriptionView.subscription.plan.kmFee')}
              </Box>
              <Box sx={{ display: 'inline' }}>
                {selectedPlan?.kmPriceExclVat
                  ? `${formatMoney(selectedPlan.kmPriceExclVat, currency, i18n.language)}/km`
                  : t('terms.attributes.queryForPricing')}
              </Box>
            </Box>
          </>
        )}
        {isPlanUndefined && <p>{t('SubscriptionView.subscription.noSubscription')}</p>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem', mt: '2rem' }}>
          <Button
            fullWidth
            type='submit'
            variant='contained'
            color='primary'
            disableElevation
            disabled={isSubmitDisabled}
          >
            {isPlanUndefined
              ? t('SubscriptionView.subscription.actions.activatePlan')
              : t('SubscriptionView.subscription.actions.updatePlan')}
          </Button>
          {!isPlanUndefined && (
            <Button
              fullWidth
              type='submit'
              variant='outlined'
              color='primary'
              disableElevation
              disabled={isPlanUndefined}
              onClick={() => handleRequestCancelPlan()}
            >
              {t('SubscriptionView.subscription.actions.cancelPlan')}
            </Button>
          )}
        </Box>
      </form>
    </PaperLayout>
  );
}

export default AccountSubscriptionPlanForm;
